import { DateTime } from '@ksyos/date-time';
import { Button, IconCircle, Link } from '@ksyos/design-system';
import { imageAndPdfExtensions } from '@common/utils/fileExtensions';
import type { ConclusieDiagnoseOption } from '../../../../../../common/enums/conclusieDiagnoseOption';
import type { IOphthalmologyAsset } from '../../../../../../common/validation/customControls/ophthalmology';
import type { IMedication } from '../../../../interfaces/medication';
import type { IProcess } from '../../../../interfaces/process';
import type { IEpisodeContext } from '../../../episodes/EpisodeContext';
import type { ICustomControlRequestAddEditProps } from '../../interfaces/';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { FilesUrlBase } from '../../../../../../common/enums/filesUrlBase';
import { PractitionerRoleInRequest } from '../../../../../../common/enums/practitionerRoleInRequest';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { OphthalmologyFilePlacement } from '../../../../../../common/validation/customControls/ophthalmology';
import { getJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { useEpisodeContext } from '../../../../../utils/hooks/useEpisodeContext';
import { showServiceToast, showToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    Form,
    FormDivider,
    HeaderDivider,
    InputCheckbox,
    Paragraph,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FlexColumns } from '../../../../elements/FlexColumns';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormGroup } from '../../../../elements/forms/FormGroup';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { Modal } from '../../../../elements/modals/Modal';
import { DateField } from '../../../../fields/Date';
import { RadioGroupField } from '../../../../fields/RadioGroup';
import { TextAreaField } from '../../../../fields/TextArea';
import { EvaluationRequestItem } from '../../../evaluation/EvaluationRequestItem';
import { ProfessionalSummaryData } from '../../../professionalSummaryData/ProfessionalSummaryData';
import { getFilesByPlacement } from '../../TFO/v1/getFilesByPlacement';
import { ServiceOrganizationProvider } from '../../utils';
import { AssetDropzoneWithActions } from '../../utils/v1/AssetDropzoneWithActions';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { DiagnosisConclusionSelection } from '../../utils/v1/DiagnosisConclusionSelection';
import { ExistingExaminationModal } from '../../utils/v1/ExistingExaminationModal';
import { MedicationsAddEdit } from '../../utils/v1/medications/MedicationsAddEdit';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceConsumerSupervisorSelector } from '../../utils/v1/ServiceConsumerSupervisorSelector';

interface IInnerTfcV1RequestAddEditState {
    buttonDisabled?: boolean;
    isExaminationModalOpen: boolean;
    isPreviousExaminationSelected: boolean;
    potentialPreviousRequests: IProcess[];
    showDropzone: boolean;
    showEvaluation?: boolean;
    showModal: boolean;
    totalPotentialPreviousRequests: number;
}

export interface ITfcV1RequestAddEditCustomControlData {
    conclusie_diagnose: ConclusieDiagnoseOption;
    diabetes_sinds: string;
    files: IOphthalmologyAsset[];
    gereguleerd: 'Ja' | 'Nee' | null;
    toelichting: string | null;
    vraag: string | null;
}

export function TfcV1RequestAddEdit(props: ICustomControlRequestAddEditProps) {
    const episodeContext = useEpisodeContext();

    return (
        <InnerTfcV1RequestAddEdit
            {...props}
            {...episodeContext}
        />
    );
}

class InnerTfcV1RequestAddEdit extends Component<
    ICustomControlRequestAddEditProps & IEpisodeContext,
    IInnerTfcV1RequestAddEditState
> {
    public constructor(props: ICustomControlRequestAddEditProps & IEpisodeContext) {
        super(props);

        this.state = {
            showDropzone: !props.previousRequest || this.props.request.customControlData.files.length > 0,
            showModal: false,
            potentialPreviousRequests: [],
            totalPotentialPreviousRequests: 0,
            isExaminationModalOpen: false,
            isPreviousExaminationSelected: false,
        };
    }

    public async componentDidMount() {
        await this.fetchPotentialPreviousRequests();
        if (
            this.props.previousRequest &&
            this.props.previousRequest.customControlData &&
            this.props.previousRequest.customControlData.diabetes_sinds
        ) {
            this.props.handleChangeCustomControl({
                diabetes_sinds: this.props.previousRequest.customControlData.diabetes_sinds,
            });
        }
    }

    public render() {
        return (
            <NewOrEditRequestProcessContainer
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
                title={getServiceLabel(this.props.service.code, 'request', this.props.service.name)}
            >
                <Form onSubmit={async () => null}>
                    {this.state.showEvaluation && this.props.request.id && (
                        <EvaluationRequestItem
                            onClose={async () => {
                                const result = await getJson(
                                    `/api/organizations/${this.props.request.selectedDiagnosticReportProviderOrganizationId}`
                                );
                                showServiceToast(
                                    ToastType.success,
                                    'toast.submitSuccess',
                                    this.props.service,
                                    'request',
                                    translateKey('toast.consultRequestSubmit', result.json.name)
                                );
                                await this.props.onSave(this.props.request.id!);
                            }}
                            requestId={this.props.request.id}
                        />
                    )}

                    <CareProviderSelector
                        {...this.props}
                        entity={this.props.request}
                    />

                    <ServiceOrganizationProvider
                        errors={this.props.errors}
                        initialOrganizationId={this.props.request.selectedDiagnosticReportProviderOrganizationId}
                        initialPractitionerId={this.props.request.selectedDiagnosticReportProviderPractitionerId}
                        onChange={async (organizationId, practitionerId) => {
                            await this.props.handleChange(
                                'selectedDiagnosticReportProviderOrganizationId',
                                organizationId
                            );
                            await this.props.handleChange(
                                'selectedDiagnosticReportProviderPractitionerId',
                                practitionerId
                            );
                        }}
                        onDoValidate={async () =>
                            await this.props.validateOne('selectedDiagnosticReportProviderOrganizationId')
                        }
                        roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                        searchInNetwork
                        serviceProviderServiceId={this.props.service.id}
                    />

                    <ServiceConsumerSupervisorSelector {...this.props} />

                    <FormDivider />

                    {this.addEditForm()}

                    <ButtonContainer
                        alignRight
                        buttons={[
                            <CancelButton
                                cancel={this.props.onCancel}
                                entity={ProcessEntity.request}
                                isExisting={!!this.props.request.id}
                                serviceCode={this.props.service.code}
                                serviceType={this.props.service.serviceType}
                            />,
                            <SaveButton
                                buttonDisabled={this.state.buttonDisabled}
                                save={async () => {
                                    await this.saveRequest('draft');
                                }}
                            />,
                            <SaveSendButton
                                buttonDisabled={this.state.buttonDisabled}
                                saveSend={async () => {
                                    await this.saveRequest('final');
                                }}
                            />,
                        ]}
                    />
                </Form>
            </NewOrEditRequestProcessContainer>
        );
    }

    private async saveRequest(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedRequest = await this.props.validateRequest(saveType);
        if (!validatedRequest) {
            await this.setState({ buttonDisabled: false });

            return;
        }
        const requestId = await this.props.onSaveRequest(saveType);

        if (saveType === 'final') {
            await this.setState({ showEvaluation: true });
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'request',
                translateKey('toast.draftDefaultMessage')
            );
            await this.props.onSave(requestId);
        }
    }

    private addEditForm() {
        const customControlData: ITfcV1RequestAddEditCustomControlData = this.props.request.customControlData;

        return (
            <FormContextProvider
                value={{
                    placeholderGroupName: 'request',
                    item: customControlData,
                    errors: this.props.errors,
                    onChange: async (key, value) => await this.props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await this.props.validateOneCustomControl(key),
                }}
            >
                {this.state.showEvaluation && this.props.request.id && (
                    <EvaluationRequestItem
                        onClose={async () => {
                            await this.setState({ showEvaluation: false });
                            showServiceToast(
                                ToastType.success,
                                'toast.submitSuccess',
                                this.props.service,
                                'request',
                                translateKey('toast.submitDefaultMessage')
                            );
                            await this.props.onSave(this.props.request.id!);
                        }}
                        requestId={this.props.request.id}
                    />
                )}
                {this.dropzones()}

                <FormDivider />

                <MedicationsAddEdit
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangePatientUsesMedication={async (patientUsesMedication: boolean) =>
                        await this.props.handleChange('patientUsesMedication', patientUsesMedication)
                    }
                    onChange={async (medications: IMedication[]) =>
                        await this.props.handleChange('requestMedications', medications)
                    }
                    patientUsesMedication={this.props.request.patientUsesMedication}
                    validateMedications={async () => await this.props.validateOne('requestMedications')}
                    validatePatientUsesMedication={async () => await this.props.validateOne('patientUsesMedication')}
                />

                <ProfessionalSummaryData
                    entity={ProcessEntity.request}
                    entityId={this.props.request.id}
                    errors={this.props.errors}
                    handleChangeAllergies={async (allergies) => await this.props.handleChange('allergies', allergies)}
                    handleChangeEpisodes={async (episodesAndJournal) =>
                        await this.props.handleChange('requestEpisodesAndJournal', episodesAndJournal)
                    }
                    handleChangeLabResults={async (labResults) =>
                        await this.props.handleChange('requestLabResults', labResults)
                    }
                    validateEpisodesAndJournals={async () => await this.props.validateOne('requestEpisodesAndJournal')}
                />

                <HeaderDivider text="Vraag" />

                <RadioGroupField
                    error={this.props.errors.get('customControlData.gereguleerd')}
                    fieldKey="gereguleerd"
                    hasErrors={this.props.errors.has('customControlData.gereguleerd')}
                    label="Patiënt goed gereguleerd"
                    name="gereguleerd"
                    options={['Ja', 'Nee']}
                />

                <DateField
                    error={this.props.errors.get('customControlData.diabetes_sinds')}
                    fieldKey="diabetes_sinds"
                    hasErrors={this.props.errors.has('customControlData.diabetes_sinds')}
                    label="Diabetes sinds"
                    maxDate={DateTime.now().format()}
                    placeholder={`${translateKey('common.datePlaceholder')}`}
                    size="medium"
                />

                <DiagnosisConclusionSelection
                    conclusie_diagnose={this.props.request.customControlData.conclusie_diagnose}
                    diagnosis={this.props.request.diagnosis}
                    errors={this.props.errors}
                    handleChangeConclusieDiagnose={this.props.handleChangeCustomControl}
                    handleChangeDiagnosisConsideration={async (diagnosis) =>
                        await this.props.handleChange('diagnosis', diagnosis)
                    }
                    service={this.props.service}
                    validateConclusieDiagnose={this.props.validateOneCustomControl}
                    validateDiagnosisConsideration={async () => this.props.validateOne('diagnosis')}
                />

                <TextAreaField
                    error={this.props.errors.get('customControlData.vraag')}
                    fieldKey="vraag"
                    hasErrors={this.props.errors.has('customControlData.vraag')}
                    isRequired
                    label="Vraag"
                    placeholder="Stel een vraag aan de oogarts..."
                />

                <TextAreaField
                    error={this.props.errors.get('customControlData.toelichting')}
                    fieldKey="toelichting"
                    hasErrors={this.props.errors.has('customControlData.toelichting')}
                    label="Toelichting"
                    placeholder="Een toelichting, opmerkingen of vragen..."
                />
            </FormContextProvider>
        );
    }

    private dropzones() {
        const placeholder = "Klik of sleep je foto's in dit vak";
        const customControlData: ITfcV1RequestAddEditCustomControlData = this.props.request.customControlData;

        return (
            <>
                <HeaderDivider text="Onderzoek" />
                {this.props.previousRequest !== undefined && (
                    <FormGroup>
                        <InputCheckbox
                            checked={this.state.showDropzone}
                            onChange={async (value) => await this.onChangeShowDropzone(value)}
                            text={translateKey('procedure.sendExtraAttachments')}
                        />
                    </FormGroup>
                )}

                <Modal
                    actionButtons={[
                        <Button
                            onClick={async () => await this.setState({ showModal: false })}
                            variant="outline"
                        >
                            {translateKey('common.no')}
                        </Button>,
                        <Button
                            onClick={async () => {
                                await this.setState({ showModal: false });
                                await this.setState({ showDropzone: false });
                                await this.props.handleChangeCustomControl({ files: [] });
                            }}
                        >
                            {translateKey('common.yes')}
                        </Button>,
                    ]}
                    isOpen={this.state.showModal}
                    onClose={async () => await this.setState({ showModal: false })}
                    size="small"
                    title={translateKey('procedure.sendExtraAttachments')}
                >
                    <Paragraph>{translateKey('procedure.sureToCancelAttachments')}</Paragraph>
                </Modal>

                {this.state.showDropzone && (
                    <FormGroup>
                        <FlexColumns sm={8}>
                            <>
                                <FormLabel stacked>OD</FormLabel>
                                <AssetDropzoneWithActions
                                    acceptedFiles={imageAndPdfExtensions}
                                    assets={getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.od)}
                                    assetsBaseUrl={FilesUrlBase.requests}
                                    automation="dropzoneOd"
                                    maxFiles={4}
                                    onChange={async (assets) => {
                                        const files: IOphthalmologyAsset[] = assets.map((asset) => {
                                            return { ...asset, placement: OphthalmologyFilePlacement.od };
                                        });
                                        await this.props.handleChangeCustomControl({
                                            files: [
                                                ...files,
                                                ...getFilesByPlacement(
                                                    customControlData.files,
                                                    OphthalmologyFilePlacement.os
                                                ),
                                                ...getFilesByPlacement(
                                                    customControlData.files,
                                                    OphthalmologyFilePlacement.aanvullend
                                                ),
                                            ],
                                        });
                                    }}
                                    onUploading={async (isUploading) =>
                                        await this.setState({ buttonDisabled: isUploading })
                                    }
                                    placeholder={placeholder}
                                    processActivityId={this.props.request.id}
                                    service={this.props.service}
                                    showUseExistingExamination={false}
                                    updateNewRequestPreviousRequest={this.props.updateNewRequestPreviousRequest}
                                />
                            </>
                            <>
                                <FormLabel stacked>OS</FormLabel>
                                <AssetDropzoneWithActions
                                    acceptedFiles={imageAndPdfExtensions}
                                    assets={getFilesByPlacement(customControlData.files, OphthalmologyFilePlacement.os)}
                                    assetsBaseUrl={FilesUrlBase.requests}
                                    automation="dropzoneOs"
                                    maxFiles={4}
                                    onChange={async (assets) => {
                                        const files: IOphthalmologyAsset[] = assets.map((asset) => {
                                            return { ...asset, placement: OphthalmologyFilePlacement.os };
                                        });
                                        await this.props.handleChangeCustomControl({
                                            files: [
                                                ...getFilesByPlacement(
                                                    customControlData.files,
                                                    OphthalmologyFilePlacement.od
                                                ),
                                                ...files,
                                                ...getFilesByPlacement(
                                                    customControlData.files,
                                                    OphthalmologyFilePlacement.aanvullend
                                                ),
                                            ],
                                        });
                                    }}
                                    onUploading={async (isUploading) =>
                                        await this.setState({ buttonDisabled: isUploading })
                                    }
                                    placeholder={placeholder}
                                    processActivityId={this.props.request.id}
                                    service={this.props.service}
                                    showUseExistingExamination={false}
                                    updateNewRequestPreviousRequest={this.props.updateNewRequestPreviousRequest}
                                />
                            </>
                            <>
                                <FormLabel stacked>Aanvullend</FormLabel>
                                <AssetDropzoneWithActions
                                    acceptedFiles={imageAndPdfExtensions}
                                    assets={getFilesByPlacement(
                                        customControlData.files,
                                        OphthalmologyFilePlacement.aanvullend
                                    )}
                                    assetsBaseUrl={FilesUrlBase.requests}
                                    automation="dropzoneAdditional"
                                    maxFiles={4}
                                    onChange={async (assets) => {
                                        const files: IOphthalmologyAsset[] = assets.map((asset) => {
                                            return { ...asset, placement: OphthalmologyFilePlacement.aanvullend };
                                        });
                                        await this.props.handleChangeCustomControl({
                                            files: [
                                                ...getFilesByPlacement(
                                                    customControlData.files,
                                                    OphthalmologyFilePlacement.od
                                                ),
                                                ...getFilesByPlacement(
                                                    customControlData.files,
                                                    OphthalmologyFilePlacement.os
                                                ),
                                                ...files,
                                            ],
                                        });
                                    }}
                                    onUploading={async (isUploading) =>
                                        await this.setState({ buttonDisabled: isUploading })
                                    }
                                    placeholder={placeholder}
                                    processActivityId={this.props.request.id}
                                    service={this.props.service}
                                    showUseExistingExamination={false}
                                    updateNewRequestPreviousRequest={this.props.updateNewRequestPreviousRequest}
                                />
                            </>
                        </FlexColumns>
                        {this.state.isExaminationModalOpen && this.props.updateNewRequestPreviousRequest && (
                            <ExistingExaminationModal
                                handleClose={async (isSuccess?: boolean) => {
                                    await this.setState({ isExaminationModalOpen: false });
                                    if (isSuccess) {
                                        this.setState({ isPreviousExaminationSelected: true });
                                        showToast(ToastType.success, translateKey('toast.convertConsult'));
                                    }
                                }}
                                initialPotentialPreviousRequests={this.state.potentialPreviousRequests}
                                initialTotalPotentialPreviousRequests={this.state.totalPotentialPreviousRequests}
                                isOpen={this.state.isExaminationModalOpen}
                                patientId={this.props.patient.id}
                                serviceId={this.props.service.id}
                                serviceType={this.props.service.serviceType}
                                updateNewRequestPreviousRequest={this.props.updateNewRequestPreviousRequest}
                            />
                        )}
                        {!this.props.previousRequest && this.state.potentialPreviousRequests.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '16px' }}>
                                <IconCircle
                                    color="primary"
                                    hasBorder
                                    icon="examination"
                                />
                                <Link
                                    color="primary"
                                    ml={4}
                                    onClick={async () => await this.setState({ isExaminationModalOpen: true })}
                                    variant="button"
                                >
                                    {translateKey('common.useExistingExamination')}
                                </Link>
                            </div>
                        )}
                    </FormGroup>
                )}
            </>
        );
    }

    private async fetchPotentialPreviousRequests() {
        const result = await getJson(
            `/api/requestProcesses?patientId=${this.props.patient.id}&serviceIdForPotentialPreviousRequests=${this.props.service.id}&limit=3&orderByCreatedAtDesc=true`
        );
        await this.setState({ potentialPreviousRequests: result.json.items });
        await this.setState({ totalPotentialPreviousRequests: result.json.count });
    }

    private async onChangeShowDropzone(showDropzone: boolean) {
        if (!showDropzone && this.props.request.customControlData.files.length > 0) {
            await this.setState({ showModal: true, showDropzone: true });
        } else {
            await this.setState({ showDropzone });
        }
    }
}

export function tfcV1InitializeCustomControlData() {
    return {
        gereguleerd: null,
        diabetes_sinds: null,
        conclusie_diagnose: null,
        toelichting: null,
        files: [],
        vraag: null,
    };
}
